import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import { CongresoPublico } from '../types/congreso.types';
import { INSTITUCION_NOMBRE_COMPLETO, MARCA_NOMBRE } from '../constants';

interface NavbarProps {
  congreso?: CongresoPublico | null;
  hasAgenda?: boolean;
  hasSpeakers?: boolean;
  hasSponsors?: boolean;
  hasAccommodations?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  congreso,
  hasAgenda = true,
  hasSpeakers = true,
  hasSponsors = true,
  hasAccommodations = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/' || location.pathname.startsWith('/evento/');

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav transition-all duration-300 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo & Brand: MartillerosBA */}
          <Link to="/" className="flex items-center gap-3 group max-w-xl" title={INSTITUCION_NOMBRE_COMPLETO}>
            <div className="h-11 w-auto flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
              <img
                src="/assets/logo-martillerosba.png"
                alt="MartillerosBA"
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-extrabold text-xl tracking-wider text-brand-navy group-hover:text-brand-cyan transition-colors leading-tight">
                {MARCA_NOMBRE}
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium leading-tight line-clamp-1 sm:line-clamp-none">
                {INSTITUCION_NOMBRE_COMPLETO}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-6 text-sm font-semibold">
            {isHome ? (
              <>
                <a href="#inicio" className="text-slate-700 hover:text-brand-cyan transition-colors">
                  Inicio
                </a>
                {hasAgenda && (
                  <a href="#agenda" className="text-slate-700 hover:text-brand-cyan transition-colors">
                    Programa
                  </a>
                )}
                {hasSpeakers && (
                  <a href="#disertantes" className="text-slate-700 hover:text-brand-cyan transition-colors">
                    Disertantes
                  </a>
                )}
                {hasSponsors && (
                  <a href="#sponsors" className="text-slate-700 hover:text-brand-cyan transition-colors">
                    Sponsors
                  </a>
                )}
                <a href="#sede" className="text-slate-700 hover:text-brand-cyan transition-colors">
                  Sede
                </a>
                {hasAccommodations && (
                  <a href="#alojamiento" className="text-slate-700 hover:text-brand-cyan transition-colors">
                    Alojamiento
                  </a>
                )}
                <a href="#faq" className="text-slate-700 hover:text-brand-cyan transition-colors">
                  FAQ
                </a>
              </>
            ) : (
              <Link to="/" className="text-slate-700 hover:text-brand-cyan transition-colors">
                Volver a la Edición Actual
              </Link>
            )}

            <Link
              to="/ediciones"
              className="text-slate-600 hover:text-brand-navy transition-colors"
            >
              Ediciones Anteriores
            </Link>

            {/* Botón Principal Destacado para el usuario final */}
            <a
              href="#agenda"
              className="px-5 py-2.5 rounded-xl bg-brand-cyan hover:bg-sky-500 text-white font-bold transition-all shadow-md shadow-brand-cyan/20 flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              <span>Ver Programa</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-700 hover:text-brand-navy p-2 rounded-xl bg-slate-100 border border-slate-200"
              aria-label="Abrir menú"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {isOpen && (
        <div className="md:hidden bg-white/98 border-b border-slate-200 px-4 pt-2 pb-6 space-y-3 shadow-lg">
          {isHome ? (
            <>
              <a
                href="#inicio"
                onClick={() => setIsOpen(false)}
                className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
              >
                Inicio
              </a>
              {hasAgenda && (
                <a
                  href="#agenda"
                  onClick={() => setIsOpen(false)}
                  className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
                >
                  Programa
                </a>
              )}
              {hasSpeakers && (
                <a
                  href="#disertantes"
                  onClick={() => setIsOpen(false)}
                  className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
                >
                  Disertantes
                </a>
              )}
              {hasSponsors && (
                <a
                  href="#sponsors"
                  onClick={() => setIsOpen(false)}
                  className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
                >
                  Sponsors
                </a>
              )}
              <a
                href="#sede"
                onClick={() => setIsOpen(false)}
                className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
              >
                Sede
              </a>
              {hasAccommodations && (
                <a
                  href="#alojamiento"
                  onClick={() => setIsOpen(false)}
                  className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
                >
                  Alojamiento
                </a>
              )}
            </>
          ) : (
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
            >
              Volver a Edición Actual
            </Link>
          )}

          <Link
            to="/ediciones"
            onClick={() => setIsOpen(false)}
            className="block text-slate-700 hover:text-brand-cyan py-2 font-medium"
          >
            Ediciones Anteriores
          </Link>
        </div>
      )}
    </nav>
  );
};
